'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { ArrowLeft, Grid3X3, MapPin, Tag, X, TrendingUp } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import BottomNav from '@/components/BottomNav';
import NewsCard from '@/components/NewsCard';
import { CategoryCardSkeleton } from '@/components/LoadingSkeleton';
import { MOCK_ARTICLES, CATEGORIES, type NewsArticle } from '@/lib/mockData';
import { toast } from 'sonner';

const DISTRICT_CATEGORIES = CATEGORIES.filter((c) => c.type === 'district' || c.type === 'state');
const TOPIC_CATEGORIES = CATEGORIES.filter((c) => c.type === 'topic');

export default function CategoriesClient() {
  const [lang, setLang] = useState<'hi' | 'en'>('hi');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const storedLang = localStorage.getItem('nj-lang') as 'hi' | 'en' | null;
    if (storedLang) setLang(storedLang);

    const storedBookmarks = localStorage.getItem('nj-bookmarks');
    if (storedBookmarks) {
      try {
        setBookmarks(new Set(JSON.parse(storedBookmarks)));
      } catch {
        // ignore
      }
    }

    const timer = setTimeout(() => {
      setArticles(MOCK_ARTICLES);
      setIsLoading(false);
    }, 900);
    return () => clearTimeout(timer);
  }, []);

  const getArticleCount = (slug: string) => {
    if (slug === 'breaking') return MOCK_ARTICLES.filter((a) => a.isBreaking).length;
    return MOCK_ARTICLES.filter((a) => a.category === slug || a.district === slug).length;
  };

  const filteredArticles = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory === 'breaking') return articles.filter((a) => a.isBreaking);
    return articles.filter((a) => a.category === selectedCategory || a.district === selectedCategory);
  }, [selectedCategory, articles]);

  const selectedCategoryObj = selectedCategory ? CATEGORIES.find((c) => c.slug === selectedCategory) : null;

  const handleBookmarkToggle = (id: string) => {
    const next = new Set(bookmarks);
    if (next.has(id)) {
      next.delete(id);
      toast.info(lang === 'hi' ? 'बुकमार्क हटाया गया' : 'Bookmark removed');
    } else {
      next.add(id);
      toast.success(lang === 'hi' ? 'बुकमार्क सेव हो गया' : 'Article bookmarked');
    }
    setBookmarks(next);
    localStorage.setItem('nj-bookmarks', JSON.stringify([...next]));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="bg-primary h-1" />
        <div className="px-4 py-2.5 flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3">
            <Link href="/" className="p-2 rounded-xl hover:bg-muted transition-colors btn-press">
              <ArrowLeft size={16} className="text-muted-foreground" />
            </Link>
            <div className="flex items-center gap-2">
              <AppLogo size={28} />
              <div>
                <p className="font-hindi font-bold text-primary text-sm leading-none">नमस्ते झारखंड</p>
                <p className="text-muted-foreground text-[10px] font-hindi leading-none mt-0.5">
                  {lang === 'hi' ? 'श्रेणियाँ' : 'Categories'}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              const next = lang === 'hi' ? 'en' : 'hi';
              setLang(next);
              localStorage.setItem('nj-lang', next);
            }}
            className="px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted text-xs font-semibold transition-colors btn-press"
          >
            {lang === 'hi' ? 'EN' : 'हि'}
          </button>
        </div>
      </div>

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 py-5 pb-24 lg:pb-8 lg:px-8 xl:px-10">

        {/* Page title */}
        <div className="flex items-center gap-2 mb-6">
          <Grid3X3 size={20} className="text-primary" />
          <h1 className="font-hindi font-bold text-xl text-foreground">
            {lang === 'hi' ? 'सभी श्रेणियाँ' : 'All Categories'}
          </h1>
        </div>

        {/* District / State section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={16} className="text-primary" />
            <h2 className="font-hindi font-semibold text-base text-foreground">
              {lang === 'hi' ? 'जिलेवार खबरें' : 'District-wise News'}
            </h2>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {DISTRICT_CATEGORIES.length}
            </span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-6 gap-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <CategoryCardSkeleton key={`dist-skel-${i + 1}`} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-6 gap-3">
              {DISTRICT_CATEGORIES.map((cat) => {
                const count = getArticleCount(cat.slug);
                const isSelected = selectedCategory === cat.slug;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(isSelected ? null : cat.slug)}
                    className={`relative flex flex-col items-start p-4 rounded-2xl border transition-all duration-150 category-card-hover btn-press text-left ${
                      isSelected
                        ? 'border-2 shadow-md'
                        : 'border-border bg-card hover:border-border/60'
                    }`}
                    style={
                      isSelected
                        ? { borderColor: cat.color, backgroundColor: cat.bgColor }
                        : undefined
                    }
                  >
                    {/* Selected indicator */}
                    {isSelected && (
                      <div
                        className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px]"
                        style={{ backgroundColor: cat.color }}
                      >
                        ✓
                      </div>
                    )}

                    {/* Emoji icon */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-3"
                      style={{ backgroundColor: cat.bgColor }}
                    >
                      {cat.emoji}
                    </div>

                    {/* Labels */}
                    <p
                      className="font-hindi font-bold text-sm leading-tight mb-0.5"
                      style={{ color: isSelected ? cat.color : 'var(--foreground)' }}
                    >
                      {lang === 'hi' ? cat.labelHindi : cat.labelEnglish}
                    </p>
                    <p className="text-muted-foreground text-[10px] mb-2">
                      {lang === 'hi' ? cat.labelEnglish : cat.labelHindi}
                    </p>

                    {/* Article count */}
                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ color: cat.color, backgroundColor: cat.bgColor }}
                    >
                      {count} {lang === 'hi' ? 'खबरें' : 'articles'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Topic section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} className="text-primary" />
            <h2 className="font-hindi font-semibold text-base text-foreground">
              {lang === 'hi' ? 'विषयवार खबरें' : 'Topic-wise News'}
            </h2>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {TOPIC_CATEGORIES.length}
            </span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <CategoryCardSkeleton key={`topic-skel-${i + 1}`} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-6 gap-3">
              {TOPIC_CATEGORIES.map((cat) => {
                const count = getArticleCount(cat.slug);
                const isSelected = selectedCategory === cat.slug;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(isSelected ? null : cat.slug)}
                    className={`relative flex flex-col items-start p-4 rounded-2xl border transition-all duration-150 category-card-hover btn-press text-left ${
                      isSelected
                        ? 'border-2 shadow-md'
                        : 'border-border bg-card hover:border-border/60'
                    }`}
                    style={
                      isSelected
                        ? { borderColor: cat.color, backgroundColor: cat.bgColor }
                        : undefined
                    }
                  >
                    {isSelected && (
                      <div
                        className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px]"
                        style={{ backgroundColor: cat.color }}
                      >
                        ✓
                      </div>
                    )}

                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-3"
                      style={{ backgroundColor: cat.bgColor }}
                    >
                      {cat.emoji}
                    </div>

                    <p
                      className="font-hindi font-bold text-sm leading-tight mb-0.5"
                      style={{ color: isSelected ? cat.color : 'var(--foreground)' }}
                    >
                      {lang === 'hi' ? cat.labelHindi : cat.labelEnglish}
                    </p>
                    <p className="text-muted-foreground text-[10px] mb-2">
                      {lang === 'hi' ? cat.labelEnglish : cat.labelHindi}
                    </p>

                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ color: cat.color, backgroundColor: cat.bgColor }}
                    >
                      {count} {lang === 'hi' ? 'खबरें' : 'articles'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Filtered news results */}
        {selectedCategory && (
          <section className="fade-in">
            {/* Section header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <TrendingUp size={18} className="text-primary" />
                <div>
                  <h2 className="font-hindi font-bold text-lg text-foreground">
                    {selectedCategoryObj
                      ? (lang === 'hi' ? selectedCategoryObj.labelHindi : selectedCategoryObj.labelEnglish)
                      : ''}{' '}
                    {lang === 'hi' ? 'की खबरें' : 'News'}
                  </h2>
                  <p className="text-muted-foreground text-xs font-hindi">
                    {filteredArticles.length} {lang === 'hi' ? 'खबरें मिलीं' : 'articles found'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border hover:bg-muted text-xs font-hindi transition-colors btn-press"
              >
                <X size={12} />
                {lang === 'hi' ? 'बंद करें' : 'Close'}
              </button>
            </div>

            {/* Articles grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                {filteredArticles.map((article) => (
                  <NewsCard
                    key={article.id}
                    article={article}
                    lang={lang}
                    variant="default"
                    onBookmarkToggle={handleBookmarkToggle}
                    isBookmarked={bookmarks.has(article.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="text-5xl mb-4">
                  {selectedCategoryObj?.emoji || '📰'}
                </div>
                <h3 className="font-hindi font-bold text-lg text-foreground mb-2">
                  {lang === 'hi' ? 'कोई खबर नहीं मिली' : 'No articles found'}
                </h3>
                <p className="text-muted-foreground text-sm font-hindi max-w-xs mb-4">
                  {lang === 'hi'
                    ? `${selectedCategoryObj ? (lang === 'hi' ? selectedCategoryObj.labelHindi : selectedCategoryObj.labelEnglish) : ''} में अभी कोई खबर उपलब्ध नहीं है। NewsAPI key जोड़ने के बाद लाइव खबरें दिखेंगी।`
                    : `No articles available in this category yet. Add a NewsAPI key to see live news.`}
                </p>
                <Link
                  href="/"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-hindi font-medium btn-press"
                >
                  {lang === 'hi' ? 'होम पर जाएं' : 'Go to Home'}
                </Link>
              </div>
            )}
          </section>
        )}

        {/* No category selected — call to action */}
        {!selectedCategory && !isLoading && (
          <div className="bg-gradient-to-r from-primary/5 to-red-50 dark:from-primary/10 dark:to-red-950/20 border border-primary/20 rounded-2xl p-5 text-center">
            <p className="text-4xl mb-3">👆</p>
            <p className="font-hindi font-semibold text-foreground text-base mb-1">
              {lang === 'hi' ? 'श्रेणी चुनें' : 'Select a Category'}
            </p>
            <p className="text-muted-foreground text-sm font-hindi">
              {lang === 'hi' ?'किसी भी जिले या विषय पर क्लिक करके उस श्रेणी की खबरें देखें' :'Click on any district or topic above to see news from that category'}
            </p>
          </div>
        )}
      </main>

      <BottomNav lang={lang} />
    </div>
  );
}